import { useMutation, useQuery, useQueryClient } from 'react-query';
import { SmartHelpAPI } from '../config/api';

// Query keys for react-query
export const QUERY_KEYS = {
  HEALTH: 'smart-help-health',
  SESSIONS: 'smart-help-sessions',
  DOCUMENTS_STATUS: 'smart-help-documents-status',
  SYSTEM_METRICS: 'smart-help-system-metrics',
  CONVERSATION_HISTORY: 'smart-help-conversation-history',
};

// Health check hook
export const useHealthCheck = (options = {}) => {
  return useQuery(
    QUERY_KEYS.HEALTH,
    () => SmartHelpAPI.healthCheck(),
    {
      refetchInterval: 30000, // Check every 30 seconds
      retry: 3,
      retryDelay: 1000,
      staleTime: 10000, // Consider data stale after 10 seconds
      ...options,
    }
  );
};

// Chat mutation hook
export const useSendMessage = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ message, sessionId, userId }) => 
      SmartHelpAPI.sendMessage(message, sessionId, userId),
    {
      // Remove all timeout restrictions for mutations
      retry: false,
      onSuccess: (data, variables) => {
        // Invalidate sessions query to refresh active sessions
        queryClient.invalidateQueries(QUERY_KEYS.SESSIONS);
        
        // Update conversation history cache if we have sessionId
        if (variables.sessionId) {
          queryClient.setQueryData(
            [QUERY_KEYS.CONVERSATION_HISTORY, variables.sessionId],
            (oldData) => {
              if (!oldData) return { messages: [data] };
              return {
                ...oldData,
                messages: [...(oldData.messages || []), data],
              };
            }
          );
        }
      },
      onError: (error, variables) => {
        console.error('Failed to send message:', error);
      },
      ...options,
    }
  );
};

// Search mutation hook
export const useSearch = (options = {}) => {
  return useMutation(
    ({ query, limit, scoreThreshold }) => 
      SmartHelpAPI.search(query, limit, scoreThreshold),
    {
      ...options,
    }
  );
};

// Sessions query hook
export const useSessions = (options = {}) => {
  return useQuery(
    QUERY_KEYS.SESSIONS,
    () => SmartHelpAPI.getSessions(),
    {
      staleTime: 30000,
      refetchInterval: 60000, // Refresh every minute
      ...options,
    }
  );
};

// Clear session mutation hook
export const useClearSession = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(
    (sessionId) => SmartHelpAPI.clearSession(sessionId),
    {
      onSuccess: (data, sessionId) => {
        // Remove conversation history from cache
        queryClient.removeQueries([QUERY_KEYS.CONVERSATION_HISTORY, sessionId]);
        
        // Refresh sessions list
        queryClient.invalidateQueries(QUERY_KEYS.SESSIONS);
      },
      ...options,
    }
  );
};

// Documents status query hook
export const useDocumentsStatus = (options = {}) => {
  return useQuery(
    QUERY_KEYS.DOCUMENTS_STATUS,
    () => SmartHelpAPI.getDocumentsStatus(),
    {
      staleTime: 60000, // 1 minute
      refetchInterval: 120000, // Refresh every 2 minutes
      ...options,
    }
  );
};

// System metrics query hook
export const useSystemMetrics = (options = {}) => {
  return useQuery(
    QUERY_KEYS.SYSTEM_METRICS,
    () => SmartHelpAPI.getSystemMetrics(),
    {
      staleTime: 30000,
      refetchInterval: 60000,
      ...options,
    }
  );
};

// Combined hook for chat interface
export const useChatInterface = (sessionId, userId = 'anonymous') => {
  const sendMessageMutation = useSendMessage();
  const clearSessionMutation = useClearSession();
  const healthQuery = useHealthCheck();

  const sendMessage = (message, options = {}) => {
    console.log('useChatInterface sendMessage called with:', { message, sessionId, userId });
    
    // Use mutateAsync instead of mutate to return a promise
    return sendMessageMutation.mutateAsync({
      message,
      sessionId,
      userId,
    });
  };

  const clearChat = () => {
    if (sessionId) {
      return clearSessionMutation.mutate(sessionId);
    }
  };

  return {
    sendMessage,
    clearChat,
    isConnected: healthQuery.data?.success && healthQuery.data?.data?.status === 'healthy',
    isLoading: sendMessageMutation.isLoading,
    error: sendMessageMutation.error || clearSessionMutation.error || healthQuery.error,
    healthStatus: healthQuery.data,
  };
};